import { CreateResourceUserRequest, DeleteResourceUserRequest, Permissions, ResourceType } from "@api/apiClient";
import { useGetApsProjectUsers } from "@api/apsUsers";
import { useCreateResourceUser, useDeleteResourceUser, useGetResourceUsers } from "@api/resourceUsers";
import DataGrid from "@components/DataGrid/DataGrid";
import useUserPermissionStore from "@hooks/useUserPermissionStore";
import i18n from "@i18n/i18n";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { Box, Snackbar, Tooltip } from "@mui/material";
import { GridActionsCellItem, GridColDef, GridComparatorFn, GridRowId, GridSortModel, gridStringOrNumberComparator } from "@mui/x-data-grid-premium";
import { useConfirm } from "material-ui-confirm";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";

interface ApsUsersGridProps {
  apsProjectId: string;
  projectId: string;
  searchText: string;
  isApsProjectMapped: boolean;
  isApsProjectMatched: boolean;
}

const ApsUsersGrid = observer((props: ApsUsersGridProps) => {
  const [openToast, setOpenToast] = useState(false);
  const [actionMessage, setActionMessage] = useState("");
  const [rows, setRows] = useState<any[]>([]);
  const upStore = useUserPermissionStore();
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const createResourceUser = useCreateResourceUser({
    onSuccess: async () => {
      setActionMessage(i18n.t("dashboard-add-user.user-tab-acc.add-user-success-message"));
      setOpenToast(true);
      accProjectUsers.refetch();
    },
  });
  const deleteResourceUser = useDeleteResourceUser({
    onSuccess: async () => {
      setActionMessage(i18n.t("dashboard-add-user.user-tab-acc.delete-success-message"));
      setOpenToast(true);
      accProjectUsers.refetch();
    },
  });
  const resourceUsers = useGetResourceUsers(props.projectId, true);
  const accProjectUsers = useGetApsProjectUsers(props.apsProjectId, props.searchText, {
    enabled: typeof props.apsProjectId !== "undefined" && props.apsProjectId !== "",
  });
  const confirm = useConfirm();

  useEffect(() => {
    const usersMap = new Map(resourceUsers.data?.map((user) => [user.email?.toLowerCase(), user.id]));
    const newRows =
      accProjectUsers.data?.results?.map((row) => ({
        ...row,
        userId: usersMap.get(row.email?.toLowerCase()) || null,
      })) || [];
    setRows(newRows);
  }, [accProjectUsers.data, resourceUsers.data]);

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
  };

  useEffect(() => {
    if (typeof props.apsProjectId !== "undefined" && props.apsProjectId !== "") {
      accProjectUsers.refetch();
    } else {
      setRows([]);
    }
  }, [props.apsProjectId, props.searchText]);

  const handleAddResourceUserClick = async (id: GridRowId) => {
    const { confirmed } = await confirm({
      title: i18n.t("dashboard-add-user.confirm-dialog-add.header"),
      description: i18n.t("dashboard-add-user.confirm-dialog-add.message"),
      confirmationText: i18n.t("dialog.yes"),
      cancellationText: i18n.t("dialog.cancel"),
      dialogProps: {
        maxWidth: "xs",
      },
      confirmationButtonProps: {
        variant: "contained",
        color: "error",
      },
      cancellationButtonProps: {
        variant: "outlined",
      },
    });

    if (confirmed) {
      const row = rows.find((row) => row.id === id);
      if (row) {
        const tmpRequest: any = new CreateResourceUserRequest({
          resourceId: props.projectId,
          apsUserId: row.id,
          apsRoles: row.roles,
          resourceType: ResourceType.Project,
          email: row.email,
        });
        await createResourceUser.mutateAsync(tmpRequest);
      }
    }
  };

  const handleRemoveResourceUserClick = async (id: GridRowId) => {
    const { confirmed } = await confirm({
      title: i18n.t("dashboard-add-user.confirm-dialog.header"),
      description: i18n.t("dashboard-add-user.confirm-dialog.message"),
      confirmationText: i18n.t("dialog.yes"),
      cancellationText: i18n.t("dialog.cancel"),
      dialogProps: {
        maxWidth: "xs",
      },
      confirmationButtonProps: {
        variant: "contained",
        color: "error",
      },
      cancellationButtonProps: {
        variant: "outlined",
      },
    });

    if (confirmed) {
      const row = rows.find((row) => row.id === id);
      if (row) {
        await deleteResourceUser.mutateAsync(new DeleteResourceUserRequest({ resourceId: props.projectId, userId: row.userId }) as any);
      }
    }
  };

  const roleSortComparator: GridComparatorFn = (v1, v2, param1, param2) => {
    return gridStringOrNumberComparator(v1 as any, v2 as any, param1, param2);
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: i18n.t("dashboard-add-user.user-tab-acc.datagrid-header-column-user-name"),
      editable: false,
      flex: 1,
    },
    {
      field: "email",
      headerName: i18n.t("dashboard-add-user.user-tab-acc.datagrid-header-column-email"),
      editable: false,
      flex: 1,
    },
    {
      field: "accrole",
      headerName: i18n.t("dashboard-add-user.user-tab-acc.datagrid-header-column-acc-role"),
      editable: false,
      flex: 1,
      renderCell: (params) => {
        const maxRolesToShow = 50; // Change this number as per your requirement
        const roles = params.row.roles.map((role: any) => role.name).join(", ");
        const displayedRoles = roles.substring(0, maxRolesToShow);
        const tooltipRoles = params.row.roles.map((role: any) => role.name).join("\n");

        if (roles.length > maxRolesToShow) {
          return (
            <Tooltip title={tooltipRoles}>
              <span>{displayedRoles} ...</span>
            </Tooltip>
          );
        } else {
          return <span>{displayedRoles}</span>;
        }
      },
      valueGetter: (_value, row) => {
        return row.roles[0]?.name;
      },
      sortComparator: roleSortComparator,
    },
    {
      field: "actions",
      type: "actions",
      headerName: i18n.t("dashboard-add-user.user-tab-acc.datagrid-header-column-action"),
      resizable: false,
      width: 80,
      getActions: ({ id, row }) => {
        if (row.userId === null || row.userId === undefined) {
          return [
            <GridActionsCellItem
              key={`add-${id}`}
              icon={
                <Tooltip title={i18n.t("dashboard-add-user.user-tab-acc.datagrid-add-button-tooltip")}>
                  <AddIcon />
                </Tooltip>
              }
              label={i18n.t("dashboard-add-user.user-tab-acc.datagrid-add-button-tooltip")}
              disabled={
                !(
                  props.isApsProjectMapped &&
                  props.isApsProjectMatched &&
                  (upStore.hasPermission(Permissions.App_Project_External_Link_Add_Users) || upStore.hasRole("ProjectSetupAdmin"))
                )
              }
              onClick={() => handleAddResourceUserClick(id)}
            />,
          ];
        } else {
          return [
            <GridActionsCellItem
              key={`cancel-${id}`}
              icon={
                <Tooltip title={i18n.t("dashboard-add-user.user-tab-acc.datagrid-delete-button-tooltip")}>
                  <RemoveIcon />
                </Tooltip>
              }
              label={i18n.t("dashboard-add-user.user-tab-acc.datagrid-delete-button-tooltip")}
              disabled={!(props.isApsProjectMapped && (upStore.hasPermission(Permissions.App_Project_External_Link_Add_Users) || upStore.hasRole("ProjectSetupAdmin")))}
              onClick={() => handleRemoveResourceUserClick(id)}
            />,
          ];
        }
      },
    },
  ];

  return (
    <>
      <Box sx={{ width: "100%", height: "100%" }}>
        <DataGrid
          loading={accProjectUsers.isFetching || accProjectUsers.isPending}
          columns={columns}
          rows={rows}
          disableColumnMenu
          density="compact"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          initialState={{
            sorting: {
              sortModel: [{ field: "email", sort: "asc" }],
            },
          }}
        />
        <Snackbar open={openToast} autoHideDuration={2000} anchorOrigin={{ vertical: "top", horizontal: "right" }} message={actionMessage} onClose={() => setOpenToast(false)} />
      </Box>
    </>
  );
});

export default ApsUsersGrid;
